import React from "react";
import { Box, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { shallow } from "zustand/shallow";
import { useParams } from "react-router-dom";

import { useServices, useSnackbar } from "contexts";
import { getInterviewAttempts } from "services";
import { getInterviewFeedbackByAttemptId } from "services/interviewFeedback";
import { Session, getAverageScore } from "utils";
import { useNavigateWithClient } from "hooks";
import useStore from "components/PerformanceReview/store";

import MobileAppShell from "../../layout/MobileAppShell";
import InterviewInstructionsModal from "../../components/InterviewInstructionsModal";
import InterviewCountdown from "../../components/InterviewCountdown";
import OverallScore from "./OverallScore";
import QuestionPerformance from "./QuestionPerformance";
import TrackAttempts from "./TrackAttempts";
import AttemptsComparison, {
  AttemptsComparisonSkeleton,
} from "./AttemptsComparison";
import QuestionFeedback from "./QuestionFeedback";

/**
 * MobileFeedback — the mobile feedback/report screen
 * (`/:client/performance/:aid`).
 *
 * Mobile equivalent of desktop `components/PerformanceReview`. Reuses the shared
 * `PerformanceReview/store` (pure state, like assessmentStore) and repeats
 * desktop's fetch flow:
 *   1. getInterviewFeedbackByAttemptId(aid) → store.init (score/speech/content/
 *      template/questions).
 *   2. getInterviewAttempts(by template) → the attempts list, for the current
 *      attempt's timing + attempt number.
 *
 * Guest/demo/personalized/tour paths from desktop are intentionally omitted —
 * mobile only handles a logged-in user viewing their own attempt.
 */
export default function MobileFeedback() {
  const { aid } = useParams();
  const service = useServices();
  const snackbar = useSnackbar();
  const navigateWithClient = useNavigateWithClient();

  const init = useStore((s) => s.init);
  const reset = useStore((s) => s.reset);
  const setTemplate = useStore((s) => s.setTemplate);
  const setAttemptId = useStore((s) => s.setAttemptId);
  const setAllAttempts = useStore((s) => s.setAllAttempts);
  const template = useStore((s) => s.template, shallow);
  const allAttempts = useStore((s) => s.allAttempts, shallow);
  const score = useStore((s) => s.score, shallow);
  const speech = useStore((s) => s.speech, shallow);
  const content = useStore((s) => s.content, shallow);

  const [loading, setLoading] = React.useState(true);
  const [attemptsLoading, setAttemptsLoading] = React.useState(false);
  const [attemptsLoaded, setAttemptsLoaded] = React.useState(false);

  React.useEffect(() => {
    reset();
    setAttemptId(aid);
    setAttemptsLoaded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aid]);

  // 1. Feedback summary.
  React.useEffect(() => {
    if (!aid) return;

    setLoading(true);
    service
      .callService(getInterviewFeedbackByAttemptId, aid)
      .then((feedback) => {
        setTemplate(feedback.template);
        init(feedback);
      })
      .catch((error) => {
        console.error(error);
        snackbar.error("Uh Oh! Unable to get your Performance analysis");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aid]);

  // 2. Attempts for this template — gives the current attempt's timing + number.
  React.useEffect(() => {
    if (!template?._id) return;

    setAttemptsLoading(true);
    service
      .callService(getInterviewAttempts, {
        user: Session.userId,
        order: "desc",
        orderBy: "createdAt",
        include: "interviewTemplate",
        interviewTemplate: template._id,
      })
      .then(({ interviewAttempts }) => {
        const finished = (interviewAttempts || []).filter(
          ({ finishedAt }) => finishedAt !== null
        );

        // Newest first, so attempt number counts down from the total.
        const mapped = finished.map((attempt, index) => {
          const c = attempt.analysis?.content?.ratings?.OVERALL || 0;
          const s = attempt.analysis?.communication?.ratings?.OVERALL || 0;
          return {
            _id: attempt._id,
            content: c,
            communication: s,
            overall: getAverageScore(s, c),
            attemptNumber: finished.length - index,
            createdAt: attempt.createdAt,
            startedAt: attempt.startedAt,
            finishedAt: attempt.finishedAt,
          };
        });

        setAllAttempts(mapped);
      })
      .catch(console.error)
      .finally(() => {
        setAttemptsLoading(false);
        setAttemptsLoaded(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?._id]);

  const currentAttempt = React.useMemo(
    () => allAttempts.find((a) => a._id === aid),
    [allAttempts, aid]
  );

  const ready = !loading && !!template?.type;

  return (
    <MobileAppShell title="Feedback">
      <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 4 }}>
        <OverallScore
          template={template}
          attempt={currentAttempt}
          score={score}
          speech={speech}
          content={content}
          loading={loading || !template?.type}
          attemptLoading={attemptsLoading}
          allAttempts={allAttempts}
          currentAttemptId={aid}
          onSelectAttempt={(id) => {
            if (id && id !== aid) navigateWithClient(`/performance/${id}`);
          }}
        />

        <QuestionPerformance loading={loading || !template?.type} />

        {/* Attempts block — mirrors desktop GraphAnalysis:
            assignment → nothing; 1 attempt → "take one more attempt" card;
            2+ attempts → attempts-comparison graph. */}
        {template?.type !== "assignment" &&
          (!attemptsLoaded ? (
            ready ? <AttemptsComparisonSkeleton /> : null
          ) : allAttempts.length > 1 ? (
            <AttemptsComparison
              allAttempts={allAttempts}
              currentAttemptId={aid}
            />
          ) : (
            <TrackAttempts template={template} />
          ))}

        {ready && <QuestionFeedback />}

        {ready && <FeedbackFooter template={template} />}
      </Box>
    </MobileAppShell>
  );
}

/** Page footer — mirrors desktop's Overall Performance / Practice Again buttons. */
function FeedbackFooter({ template }) {
  const navigateWithClient = useNavigateWithClient();
  const [showInstructions, setShowInstructions] = React.useState(false);
  const [countdown, setCountdown] = React.useState(false);

  const handleStart = () => {
    setShowInstructions(false);
    setCountdown(true);
  };

  const handleComplete = () => {
    setCountdown(false);
    if (template?._id) navigateWithClient(`/assessment?tid=${template._id}`);
  };

  return (
    <Box sx={{ display: "flex", gap: 3, pt: 2 }}>
      <Button
        fullWidth
        variant="outlined"
        startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
        onClick={() => navigateWithClient("/performance")}
      >
        Overall Performance
      </Button>
      <Button
        fullWidth
        variant="contained"
        onClick={() => setShowInstructions(true)}
        disabled={!template?._id}
      >
        Practice Again
      </Button>

      <InterviewInstructionsModal
        open={showInstructions}
        template={template}
        onClose={() => setShowInstructions(false)}
        onStart={handleStart}
      />
      <InterviewCountdown open={countdown} onComplete={handleComplete} />
    </Box>
  );
}
